// src/components/Settings.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Api as ApiIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  MenuBook as MenuBookIcon,
  Description as DescriptionIcon,
  AccountTree as AccountTreeIcon,
} from "@mui/icons-material";
import { supabase } from "../lib/supabase";
import { emailService, EmailConfig } from "../services/emailService";
import { fetchWithAuth } from "../lib/api";
import { usePersistedState } from "../hooks/usePersistedState";
import { clientStore } from "../lib/clientStorage";
import {
  PERSISTENCE_LOCAL_CLEAR_KEY,
  readPersistenceEvent,
} from "../lib/persistenceIdentity";
import PageHeaderTitle from "./PageHeaderTitle";
import { LoadingTopBar } from "./common/PageLoadingScaffold";

// Import tab components
import ProfileTab, {
  UserProfile,
  ValidationErrors,
} from "./settings/ProfileTab";
import IntegrationsTab, { GmailConfig } from "./settings/IntegrationsTab";
import EmailSettingsTab from "./settings/EmailSettingsTab";
import SecurityTab from "./settings/SecurityTab";
import DataPrivacyTab from "./settings/DataPrivacyTab";
import SystemGuideTab from "./settings/SystemGuideTab";
import TemplatesTab from "./settings/TemplatesTab";
import BotFlowsTab from "./settings/BotFlowsTab";
import {
  DEFAULT_SETTINGS_TAB,
  isSettingsTabKey,
  type SettingsTabKey,
} from "./settings/settingsTabs";
import { runtimeEnvironment } from "../lib/runtimeEnvironment";
import {
  consumeGmailCallbackOutcome,
  gmailCallbackNotice,
  gmailConnectionVerificationNotice,
  parseGmailCallbackOutcome,
} from "../lib/appNavigationState";

// Constants
const COMPANY_NAME = "LocateNow, LLC";
const COMPANY_LOGO_URL = `${runtimeEnvironment.assetBaseUrl}/locatenow-logo.png`;

const profileEmailDefaults = (profile: UserProfile): EmailConfig => ({
  agentName: `${profile.first_name} ${profile.last_name}`.trim(),
  agentEmail: profile.email,
  agentPhone: profile.phone,
  companyName: COMPANY_NAME,
  jobTitle: profile.job_title,
  logoUrl: COMPANY_LOGO_URL,
});

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: SettingsTabKey;
  value: SettingsTabKey;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 0 }}>
    {value === index && children}
  </Box>
);

// Validation helpers (exported for use in other components)
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex =
    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  return phone === "" || phoneRegex.test(phone.replace(/\s/g, ""));
};

// Settings Component
interface SettingsProps {
  identityUserId: string;
  onThemeChange?: (theme: "light" | "dark" | "system") => void;
  currentTheme?: "light" | "dark" | "system";
  isAdmin?: boolean;
  isDemo?: boolean;
  /**
   * Key-based tab selection. Inserting or reordering tabs no longer forces
   * every caller to update in lockstep -- callers address tabs by name
   * (e.g. "integrations") instead of numeric position.
   */
  defaultTab?: SettingsTabKey;
  /** Bumped by App on every deep-link navigation so re-clicking the same
   * menu item re-applies the tab even when the value hasn't changed. */
  defaultTabRequestKey?: number;
}

const Settings: React.FC<SettingsProps> = ({
  identityUserId,
  isAdmin = false,
  isDemo = false,
  defaultTab = DEFAULT_SETTINGS_TAB,
  defaultTabRequestKey = 0,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Persisted under a new key ("settings.activeTabKey") to avoid inheriting
  // the old numeric-index values from the pre-refactor layout. Users land on
  // Profile once after upgrade; subsequent visits remember their last tab.
  const [activeTab, setActiveTab] = usePersistedState<SettingsTabKey>(
    "settings.activeTabKey",
    defaultTab,
    {
      serialize: (v) => v,
      deserialize: (raw) => (isSettingsTabKey(raw) ? raw : DEFAULT_SETTINGS_TAB),
    }
  );
  // Apply a deep-linked tab ONLY when the deep-link value itself changes.
  // Including the persisted-state setter in the deps re-ran this on every
  // render, forcing the tab back to the deep-linked value — after "Email
  // Templates" the Section dropdown could never leave Templates (Grant
  // 2026-07-11).
  useEffect(() => {
    if (
      defaultTab &&
      defaultTab !== DEFAULT_SETTINGS_TAB &&
      (isAdmin || (defaultTab !== "botFlows" && defaultTab !== "systemGuide"))
    ) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, defaultTabRequestKey, isAdmin]);

  useEffect(() => {
    if (!isAdmin && (activeTab === "botFlows" || activeTab === "systemGuide")) {
      setActiveTab(DEFAULT_SETTINGS_TAB);
    }
  }, [activeTab, isAdmin, setActiveTab]);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  // Profile state
  const [profile, setProfile] = useState<UserProfile>({
    id: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    company_name: COMPANY_NAME,
    job_title: "",
    avatar_url: null,
  });
  const profileRef = React.useRef(profile);
  profileRef.current = profile;

  // Email config state
  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    agentName: "",
    agentEmail: "",
    agentPhone: "",
    companyName: COMPANY_NAME,
    jobTitle: "",
    logoUrl: COMPANY_LOGO_URL,
  });

  // Gmail integration state
  const [gmailConfig, setGmailConfig] = useState<GmailConfig>({
    connected: false,
    email: null,
    lastSync: null,
  });
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailStatusLoading, setGmailStatusLoading] = useState(false);

  const gmailBootstrapStartedRef = React.useRef(false);

  // Load Settings and consume any OAuth callback as one bootstrap operation.
  // The ref keeps React StrictMode's effect replay from issuing duplicate
  // status reads or displaying the callback twice.
  useEffect(() => {
    if (gmailBootstrapStartedRef.current) return;
    gmailBootstrapStartedRef.current = true;

    loadUserProfile();
    const gmailStatus = parseGmailCallbackOutcome(window.location.search);
    if (!gmailStatus) {
      void checkGmailConnection();
      return;
    }

    // Consume before displaying. React StrictMode replays mount effects in
    // development; removing only this outcome first keeps the notice exactly
    // once while preserving `?page=settings` and unrelated query state.
    const cleanUrl = consumeGmailCallbackOutcome(window.location.href);
    window.history.replaceState(window.history.state, "", cleanUrl);

    if (gmailStatus === "connected") {
      // The redirect says the callback completed; the status endpoint says
      // whether the connection is actually readable now. Never show success
      // beside a disconnected integration or after a failed verification.
      void checkGmailConnection().then((connected) => {
        const notice = gmailConnectionVerificationNotice(connected);
        showSnackbar(notice.message, notice.severity);
      });
      return;
    }

    // invalid_state covers malformed/forged state as well as expiry. The
    // backend-authoritative TTL is 15 minutes (reasoned, not measured).
    const notice = gmailCallbackNotice(gmailStatus);
    showSnackbar(notice.message, notice.severity);
    void checkGmailConnection();
  }, []);

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const loadUserProfile = async () => {
    setProfileLoading(true);
    const contactIdentity = emailService.captureIdentity(identityUserId);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user?.id === identityUserId) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (!emailService.isIdentityCurrent(contactIdentity)) return;

        const loadedProfile: UserProfile = {
          id: user.id,
          email: user.email || "",
          first_name:
            profileData?.first_name || user.user_metadata?.first_name || "",
          last_name:
            profileData?.last_name || user.user_metadata?.last_name || "",
          phone: profileData?.phone || "",
          company_name: COMPANY_NAME,
          job_title: profileData?.job_title || "",
          avatar_url: profileData?.avatar_url || null,
        };

        setProfile(loadedProfile);

        // Rehydrate the identity-fenced singleton from saved config or profile
        // defaults. After Clear Local Data, the clear-generation marker keeps
        // those defaults memory-only until the user explicitly saves again.
        emailService.hydrateDefaults(contactIdentity, {
          agentName: `${loadedProfile.first_name} ${loadedProfile.last_name}`.trim(),
          agentEmail: loadedProfile.email,
          agentPhone: loadedProfile.phone,
          jobTitle: loadedProfile.job_title,
        });

        // Load the active identity's configuration first.
        const savedConfig = emailService.getConfig();

        // Use saved config if it has data, otherwise use profile data
        const agentName =
          savedConfig.agentName ||
          `${loadedProfile.first_name} ${loadedProfile.last_name}`.trim();
        const agentEmail = savedConfig.agentEmail || loadedProfile.email;
        const agentPhone = savedConfig.agentPhone || loadedProfile.phone;
        const jobTitle = savedConfig.jobTitle || loadedProfile.job_title;

        setEmailConfig({
          agentName,
          agentEmail,
          agentPhone,
          companyName: COMPANY_NAME,
          jobTitle,
          logoUrl: COMPANY_LOGO_URL,
        });
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    } finally {
      setProfileLoading(false);
    }
  };
  const loadUserProfileRef = React.useRef(loadUserProfile);
  loadUserProfileRef.current = loadUserProfile;

  // Clear Local Data is a same-account cross-tab event. Reset this mounted
  // Settings mirror synchronously, then rebind after the originating clear has
  // finished deleting durable payload. Otherwise a sibling Email tab can save
  // the old custom contact values back into the new generation.
  useEffect(() => clientStore.subscribe((key, raw) => {
    if (key !== PERSISTENCE_LOCAL_CLEAR_KEY || !raw) return;
    const event = readPersistenceEvent(raw, "local-clear");
    if (event?.userId !== identityUserId) return;
    const safeDefaults = profileEmailDefaults(profileRef.current);
    setEmailConfig(safeDefaults);
    emailService.resetUserConfig();
    queueMicrotask(() => {
      const identity = emailService.setUserId(identityUserId);
      emailService.hydrateDefaults(identity, safeDefaults);
      void loadUserProfileRef.current();
    });
  }), [identityUserId]);

  const checkGmailConnection = async (): Promise<boolean | null> => {
    setGmailStatusLoading(true);
    try {
      const data = await fetchWithAuth<{
        connected: boolean;
        email: string | null;
        connected_at: string | null;
        last_used_at: string | null;
      }>("/gmail/status");

      setGmailConfig({
        connected: data.connected,
        email: data.email,
        lastSync: data.last_used_at || data.connected_at,
      });
      return data.connected;
    } catch (error) {
      console.error("Error checking Gmail connection:", error);
      return null;
    } finally {
      setGmailStatusLoading(false);
    }
  };

  // Save profile and sync to email config
  const saveProfile = async () => {
    if (profile.phone && !validatePhone(profile.phone)) {
      showSnackbar("Please fix validation errors before saving", "error");
      return;
    }

    const contactIdentity = emailService.captureIdentity(identityUserId);
    if (
      profile.id !== identityUserId ||
      !emailService.isIdentityCurrent(contactIdentity)
    ) {
      showSnackbar("Your account changed. Reload Settings before saving.", "error");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from("profiles").upsert({
        id: profile.id,
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        company_name: COMPANY_NAME,
        job_title: profile.job_title,
        avatar_url: profile.avatar_url,
        updated_at: new Date().toISOString(),
      });

      if (error) throw error;

      if (!emailService.isIdentityCurrent(contactIdentity)) return;

      // Update email config state AND save to localStorage
      const newAgentName = `${profile.first_name} ${profile.last_name}`.trim();
      const updatedEmailConfig: EmailConfig = {
        ...emailConfig,
        agentName: newAgentName,
        agentPhone: profile.phone,
        agentEmail: profile.email,
        jobTitle: profile.job_title,
      };

      setEmailConfig(updatedEmailConfig);

      // Save to localStorage via emailService
      const contactSaved = emailService.setConfig(contactIdentity, {
        agentName: updatedEmailConfig.agentName,
        agentEmail: updatedEmailConfig.agentEmail,
        agentPhone: updatedEmailConfig.agentPhone,
        jobTitle: updatedEmailConfig.jobTitle,
      });

      if (!contactSaved) {
        showSnackbar(
          "Profile saved, but this browser could not persist the email identity.",
          "warning"
        );
        return;
      }

      showSnackbar("Profile saved successfully!", "success");
    } catch (error: any) {
      console.error("Error saving profile:", error);
      showSnackbar(error.message || "Failed to save profile", "error");
    } finally {
      setLoading(false);
    }
  };

  // Save email config to localStorage
  const saveEmailConfig = () => {
    if (emailConfig.agentEmail && !validateEmail(emailConfig.agentEmail)) {
      showSnackbar("Please fix validation errors before saving", "error");
      return;
    }
    if (emailConfig.agentPhone && !validatePhone(emailConfig.agentPhone)) {
      showSnackbar("Please fix validation errors before saving", "error");
      return;
    }

    try {
      const contactIdentity = emailService.captureIdentity(identityUserId);
      const contactSaved = emailService.setConfig(contactIdentity, {
        agentName: emailConfig.agentName,
        agentEmail: emailConfig.agentEmail,
        agentPhone: emailConfig.agentPhone,
        jobTitle: emailConfig.jobTitle,
      });

      if (!contactSaved) {
        showSnackbar(
          "Your account changed or durable storage is unavailable. Reload Settings before saving.",
          "error"
        );
        return;
      }

      showSnackbar("Email settings saved!", "success");
    } catch (error: any) {
      showSnackbar("Failed to save email settings", "error");
    }
  };

  // Gmail handlers
  const handleConnectGmail = async () => {
    setGmailLoading(true);
    try {
      const data = await fetchWithAuth<{ url: string }>("/gmail/auth-url");
      window.location.href = data.url;
    } catch (error: any) {
      showSnackbar(error.message || "Failed to connect Gmail", "error");
      setGmailLoading(false);
    }
  };

  const handleDisconnectGmail = async () => {
    setGmailLoading(true);
    try {
      await fetchWithAuth<{ success: boolean }>("/gmail/disconnect", {
        method: "POST",
      });

      setGmailConfig({
        connected: false,
        email: null,
        lastSync: null,
      });

      showSnackbar("Gmail disconnected successfully", "success");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to disconnect Gmail", "error");
    } finally {
      setGmailLoading(false);
    }
  };

  // Reload settings (called after clearing local data)
  const handleReloadSettings = () => {
    // Replace the mounted custom-config mirror synchronously. If the follow-up
    // profile read fails or the user immediately opens Email Settings, cleared
    // local contact values cannot be displayed or saved again.
    const safeDefaults = profileEmailDefaults(profile);
    setEmailConfig(safeDefaults);
    const identity = emailService.captureIdentity(identityUserId);
    emailService.hydrateDefaults(identity, safeDefaults);
    void loadUserProfile();
  };

  const topBarLoading =
    loading || gmailLoading || profileLoading || gmailStatusLoading;

  return (
    <Box sx={{ pb: 4, "& .MuiInputBase-root": { fontSize: 14 }, "& .MuiTypography-body1": { fontSize: 14 } }}>
      <LoadingTopBar loading={topBarLoading} />

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <PageHeaderTitle
          icon={<SettingsIcon />}
          title="Settings"
          titleVariant="h5"
        />
      </Box>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        {isMobile ? (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <FormControl fullWidth size="small" disabled={isDemo}>
              <InputLabel id="settings-section-label">Section</InputLabel>
              <Select
                labelId="settings-section-label"
                value={activeTab}
                label="Section"
                onChange={(e) => {
                  const next = e.target.value;
                  if (isSettingsTabKey(next)) setActiveTab(next);
                }}
                renderValue={(v) => {
                  const items: Record<
                    SettingsTabKey,
                    { label: string; icon: React.ReactNode }
                  > = {
                    profile: { label: "Profile", icon: <PersonIcon fontSize="small" /> },
                    integrations: { label: "Integrations", icon: <ApiIcon fontSize="small" /> },
                    email: { label: "Email Settings", icon: <EmailIcon fontSize="small" /> },
                    templates: { label: "Templates", icon: <DescriptionIcon fontSize="small" /> },
                    botFlows: { label: "Bot Flows", icon: <AccountTreeIcon fontSize="small" /> },
                    security: { label: "Security", icon: <LockIcon fontSize="small" /> },
                    data: { label: "Data & Privacy", icon: <SecurityIcon fontSize="small" /> },
                    systemGuide: { label: "System Guide", icon: <MenuBookIcon fontSize="small" /> },
                  };
                  const item =
                    (isSettingsTabKey(v) ? items[v] : undefined) ?? items.profile;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {item.icon}
                      <Typography variant="body2" fontWeight={500}>
                        {item.label}
                      </Typography>
                    </Box>
                  );
                }}
              >
                <MenuItem value="profile">
                  <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Profile
                </MenuItem>
                <MenuItem value="integrations">
                  <ApiIcon fontSize="small" sx={{ mr: 1 }} /> Integrations
                </MenuItem>
                <MenuItem value="email">
                  <EmailIcon fontSize="small" sx={{ mr: 1 }} /> Email Settings
                </MenuItem>
                <MenuItem value="templates">
                  <DescriptionIcon fontSize="small" sx={{ mr: 1 }} /> Templates
                </MenuItem>
                {isAdmin && (
                  <MenuItem value="botFlows">
                    <AccountTreeIcon fontSize="small" sx={{ mr: 1 }} /> Bot Flows
                  </MenuItem>
                )}
                <MenuItem value="security">
                  <LockIcon fontSize="small" sx={{ mr: 1 }} /> Security
                </MenuItem>
                <MenuItem value="data">
                  <SecurityIcon fontSize="small" sx={{ mr: 1 }} /> Data & Privacy
                </MenuItem>
                {isAdmin && (
                  <MenuItem value="systemGuide">
                    <MenuBookIcon fontSize="small" sx={{ mr: 1 }} /> System Guide
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        ) : (
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              if (isSettingsTabKey(v)) setActiveTab(v);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { minHeight: 64 },
            }}
          >
            <Tab
              value="profile"
              label={isDemo ? "Profile (Demo)" : "Profile"}
              disabled={isDemo}
            />
            <Tab
              value="integrations"
              label={isDemo ? "Integrations (Demo)" : "Integrations"}
              disabled={isDemo}
            />
            <Tab
              value="email"
              label={isDemo ? "Email Settings (Demo)" : "Email Settings"}
              disabled={isDemo}
            />
            <Tab
              value="templates"
              label={isDemo ? "Templates (Demo)" : "Templates"}
              disabled={isDemo}
            />
            {isAdmin && (
              <Tab
                value="botFlows"
                label={isDemo ? "Bot Flows (Demo)" : "Bot Flows"}
                disabled={isDemo}
              />
            )}
            <Tab
              value="security"
              label={isDemo ? "Security (Demo)" : "Security"}
              disabled={isDemo}
            />
            <Tab
              value="data"
              label={isDemo ? "Data & Privacy (Demo)" : "Data & Privacy"}
              disabled={isDemo}
            />
            {isAdmin && (
              <Tab
                value="systemGuide"
                label="System Guide"
              />
            )}
          </Tabs>
        )}

        <Box sx={{ p: 2 }}>
          {isDemo && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                Settings are not available in demo mode.
              </Typography>
            </Box>
          )}
          {/* Profile Tab */}
          {!isDemo && <><TabPanel value={activeTab} index="profile">
            <ProfileTab
              profile={profile}
              setProfile={setProfile}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              loading={loading}
              onSave={saveProfile}
            />
          </TabPanel>

          {/* Integrations Tab */}
          <TabPanel value={activeTab} index="integrations">
            <IntegrationsTab
              gmailConfig={gmailConfig}
              gmailLoading={gmailLoading}
              onConnectGmail={handleConnectGmail}
              onDisconnectGmail={handleDisconnectGmail}
            />
          </TabPanel>

          {/* Email Settings Tab */}
          <TabPanel value={activeTab} index="email">
            <EmailSettingsTab
              emailConfig={emailConfig}
              setEmailConfig={setEmailConfig}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              onSave={saveEmailConfig}
            />
          </TabPanel>

          {/* Templates Tab */}
          <TabPanel value={activeTab} index="templates">
            <TemplatesTab />
          </TabPanel>

          {/* Bot Flows Tab (PKT-0035 · Phase 1 MVP slice) */}
          {isAdmin && (
            <TabPanel value={activeTab} index="botFlows">
              <BotFlowsTab />
            </TabPanel>
          )}

          {/* Security Tab */}
          <TabPanel value={activeTab} index="security">
            <SecurityTab onShowSnackbar={showSnackbar} />
          </TabPanel>

          {/* Data & Privacy Tab */}
          <TabPanel value={activeTab} index="data">
            <DataPrivacyTab
              identityUserId={identityUserId}
              emailConfig={emailConfig}
              onShowSnackbar={showSnackbar}
              onReloadSettings={handleReloadSettings}
              isAdmin={isAdmin}
            />
          </TabPanel>

          {/* System Guide Tab (admin only) */}
          {isAdmin && (
            <TabPanel value={activeTab} index="systemGuide">
              <SystemGuideTab />
            </TabPanel>
          )}
          {/* Close !isDemo block */}
          </> }
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
